import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getSeoForPath, type Lang } from '@/lib/seo';

function upsertMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function setOrRemoveMeta(attr: 'name' | 'property', key: string, content?: string) {
  const el = document.head.querySelector<HTMLMetaElement>(`meta[${attr}="${key}"]`);
  if (content) {
    if (!el) {
      const created = document.createElement('meta');
      created.setAttribute(attr, key);
      created.setAttribute('content', content);
      document.head.appendChild(created);
    } else {
      el.setAttribute('content', content);
    }
  } else if (el) {
    el.remove();
  }
}

function upsertCanonical(href: string) {
  let el = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!el) {
    el = document.createElement('link');
    el.setAttribute('rel', 'canonical');
    document.head.appendChild(el);
  }
  el.setAttribute('href', href);
}

export default function SEOHead() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const seo = getSeoForPath(location.pathname, i18n.language as Lang);

    document.title = seo.title;
    upsertMeta('name', 'description', seo.description);
    upsertMeta('name', 'keywords', seo.keywords);
    upsertCanonical(seo.canonical);

    upsertMeta('property', 'og:title', seo.title);
    upsertMeta('property', 'og:description', seo.description);
    upsertMeta('property', 'og:url', seo.canonical);
    upsertMeta('property', 'og:type', seo.ogType);
    setOrRemoveMeta('property', 'og:image', seo.ogImage);

    upsertMeta('name', 'twitter:title', seo.title);
    upsertMeta('name', 'twitter:description', seo.description);
    setOrRemoveMeta('name', 'twitter:image', seo.ogImage);
  }, [location.pathname, i18n.language]);

  return null;
}